import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Megaphone, EyeOff, SlidersHorizontal } from "lucide-react";

import { text } from "@/lib/memberDerive";
import { formatDateTime } from "@/lib/format";
import { normalizePostList, postsPath, confirmedGroups, audienceLabel } from "@/lib/community";
import { readCommunityFilter, communityQuery, activeFilterCount, searchPosts } from "@/lib/v3Views";
import { isGroupPaused } from "@/lib/groupMembership";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable } from "../components/kit";
import { V3Title, V3Columns, V3Actions, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3_CARD, PersonCard, AreaCard, ResponsibleCard, Pill, FilterBar, SearchField, ResultTable, ResultRow, V3Select } from "../components/v3app";
import { ProtectedImage } from "../components/ProtectedImage";

const AREA_LABEL = { all: "Alle für mich sichtbaren", community: "Nur Mitglieder-Community", group: "Aus einem meiner Kreise" };
const REACH = { group: "Nur für diesen Kreis", community: "Für alle Mitglieder der Community" };

export function postContext(post) {
  return [post.audience.type === "group" ? post.audience.groupName || "Kreis" : post.audience.type === "community" ? "Mitglieder-Community" : "Zielgruppe unbekannt", post.createdAt && formatDateTime(post.createdAt)].filter(Boolean).join(" · ");
}

export function PostMeta({ post }) {
  return <PersonCard name={post.isMine ? `${post.author.name} (du)` : post.author.name} context={`${postContext(post)}${post.editedAt ? " · bearbeitet" : ""}`} testid={`post-author-${post.id}`} />;
}

export function postFooter(post) {
  const count = post.commentsCount === null ? null : `${post.commentsCount} ${post.commentsCount === 1 ? "Kommentar" : "Kommentare"}`;
  return [count, REACH[post.audience.type]].filter(Boolean).join(" · ");
}

function PostCard({ post }) {
  const hidden = post.status === "hidden";
  return (
    <li className="space-y-[16px]" data-testid={`community-post-${post.id}`}>
      <PostMeta post={post} />
      {hidden ? (
        <p className="flex gap-2 rounded-[12px] bg-v3-sand px-[16px] py-[12px] text-[14px] text-v3-amber" data-testid={`community-post-${post.id}-hidden`}><EyeOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {post.isMine ? `Dein Beitrag wurde von der Moderation ausgeblendet.${post.moderationNote ? ` Hinweis: ${post.moderationNote}` : ""}` : "Dieser Beitrag ist ausgeblendet."}</p>
      ) : <p className="line-clamp-6 whitespace-pre-wrap break-words px-[2px] text-[19px] font-bold leading-[26px] text-v3-ink lg:text-[22px] lg:leading-[31px]" data-testid={`community-post-${post.id}-body`}>{post.body}</p>}
      {!hidden && post.image && <ProtectedImage raw={post.image} testid={`community-post-${post.id}-image`} />}
      <div className="flex flex-wrap items-center justify-between gap-x-4">
        <p className="text-[13.5px] text-v3-muted">{hidden ? REACH[post.audience.type] : postFooter(post)}</p>
        <Link to={`/community/beitraege/${encodeURIComponent(post.id)}`} data-testid={`community-post-${post.id}-open`} className="inline-flex min-h-[44px] items-center text-[14px] font-bold text-v3-magenta hover:underline">
          Beitrag öffnen<span className="sr-only">: {post.author.name}</span>
        </Link>
      </div>
    </li>
  );
}

// M06: filter from the URL maps 1:1 to the Core query (audience, group_id); the text search only narrows the loaded posts.
function PostList({ f }) {
  const posts = useCore(f.filter === "group" && !f.groupId ? null : postsPath(f.filter, f.groupId), (d) => ({ list: normalizePostList(d) }));
  const list = posts.data?.list;
  const shown = list && f.q ? searchPosts(list, f.q) : list;
  if (f.filter === "group" && !f.groupId) return <p className="text-[14px] text-v3-muted" data-testid="community-pick-group">Wähle unter „Ansicht filtern“ einen Kreis, in dem du bestätigtes Mitglied bist.</p>;
  if (posts.error === "forbidden") return <Unavailable testid="community-posts-forbidden">Kein Zugriff auf diese Beiträge. Es werden keine Inhalte angezeigt.</Unavailable>;
  if (posts.error === "notfound") return <Unavailable testid="community-posts-notfound">Diese Beiträge sind nicht (mehr) verfügbar.</Unavailable>;
  if (posts.error) return <ConnectionError onRetry={posts.reload} />;
  if (!posts.data) return <Loading label="Beiträge werden geladen …" />;
  if (list === null) return <Unavailable testid="community-posts-format">Die Antwort des Servers hat ein unerwartetes Format. Es werden keine Beiträge angezeigt.</Unavailable>;
  if (!shown.length) return <p className={`${V3_CARD} px-[18px] py-[16px] text-[14px] text-v3-muted`} data-testid="community-posts-empty">{f.q ? "Keine Beiträge passen zu deiner Suche." : "Noch keine Beiträge. Hier erscheinen Beiträge, sobald jemand aus deiner Zielgruppe etwas teilt."}</p>;
  return <ul className="space-y-[40px]" data-testid="community-post-list">{shown.map((p) => <PostCard key={p.id} post={p} />)}</ul>;
}

function Announcements() {
  const feed = useCore("/member/feed", (d) => d.announcements || []);
  if (feed.error) return <ConnectionError onRetry={feed.reload} />;
  if (!feed.data || !feed.data.length) return null;
  return (
    <section className="mt-[40px] space-y-[11px]" data-testid="community-announcements" aria-labelledby="community-hints">
      <h2 id="community-hints" className="flex items-center gap-[10px] text-[19px] font-bold text-v3-ink"><Megaphone className="h-[18px] w-[18px] text-v3-magenta" aria-hidden="true" />Hinweise von VISIBILIS</h2>
      {feed.data.map((a) => <div key={a.id} className={`${V3_CARD} px-[18px] py-[14px]`}><p className="font-bold text-v3-ink">{text(a.title)}</p><p className="mt-1 text-[14px] text-v3-muted">{text(a.body)}</p></div>)}
    </section>
  );
}

function areaText(f, groups) {
  if (f.filter !== "group") return AREA_LABEL[f.filter];
  const g = confirmedGroups(groups).find((x) => String(x.id) === f.groupId);
  return g ? `Kreis: ${text(g.name)}` : AREA_LABEL.group;
}

// M06 / M49: member posts from /member/posts plus VISIBILIS announcements from the feed.
export default function Community() {
  const { member } = useMemberAuth();
  const { search } = useLocation();
  const f = readCommunityFilter(search);
  const groups = useCore("/member/groups", (d) => d.groups || []);
  const area = areaText(f, groups.data) + (f.q ? ` · Suche „${f.q}“` : "");
  const main = (<>
    <FilterBar testid="community-filterbar" end={activeFilterCount(f) > 0 && <Link to="/community" data-testid="community-filter-reset" className="inline-flex min-h-[44px] items-center px-[12px] text-[13px] font-bold text-v3-magenta hover:underline">Zurücksetzen</Link>}>
      <Pill tone="ink" testid="community-area">{area}</Pill>
      <Link to={`/community/filtern${search}`} data-testid="community-filter-open" className="inline-flex min-h-[44px] items-center gap-2 px-2 text-[13px] font-bold text-v3-magenta hover:underline"><SlidersHorizontal className="h-4 w-4" aria-hidden="true" />Ansicht filtern</Link>
    </FilterBar>
    <div className="mt-[16px]"><PostList key={`${member?.id || "anon"}${search}`} f={f} /></div>
    <V3Actions className="mt-[24px]"><Link to="/community/neu" data-testid="community-create" className={V3_PRIMARY}>Beitrag schreiben</Link></V3Actions>
    <Announcements />
  </>);
  return (
    <div data-m="M06" data-testid="community-page">
      <V3Title title="Community und Beiträge" intro="Beiträge aus der Mitglieder-Community und deinen aufgenommenen Kreisen." />
      <V3Columns layout="feed" main={main} aside={<><AreaCard text={area} testid="community-area-card" /><ResponsibleCard /></>} />
    </div>
  );
}

function AreaPicker({ draft, setDraft, groups }) {
  const mine = confirmedGroups(groups.data);
  return (
    <div className="mt-[11px] space-y-[10px] rounded-[10px] border border-v3-line bg-white px-[16px] py-[16px]" data-testid="community-filters">
      <fieldset><legend className="mb-[10px] text-[14px] font-bold text-v3-ink">Beiträge anzeigen</legend>
        {Object.entries(AREA_LABEL).map(([k, label]) => (
          <label key={k} className="flex min-h-[44px] cursor-pointer items-center gap-[12px] text-[15px] text-v3-ink">
            <input type="radio" name="cf-audience" checked={draft.filter === k} onChange={() => setDraft({ ...draft, filter: k, groupId: "" })} data-testid={`community-filter-audience-${k}`} className="h-5 w-5 accent-[#D40A57]" />{label}
          </label>))}
      </fieldset>
      {draft.filter === "group" && (
        <V3Select id="cf-group" label="Kreis" testid="community-filter-group" value={draft.groupId} onChange={(e) => setDraft({ ...draft, groupId: e.target.value })}>
          <option value="">{mine.length ? "Bitte auswählen" : "Du bist in keinem Kreis bestätigtes Mitglied"}</option>
          {mine.map((g) => <option key={g.id} value={g.id}>{text(g.name)}{isGroupPaused(g) ? " – pausiert, nur lesen" : ""}</option>)}
        </V3Select>
      )}
    </div>
  );
}

const Note = ({ children, testid }) => <div className="mt-[11px]"><Unavailable testid={testid}>{children}</Unavailable></div>;

// M49 – filter view; "Filter anwenden" writes the choice into the /community URL, nothing is stored.
export function CommunityFilter() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const groups = useCore("/member/groups", (d) => d.groups || []);
  const [draft, setDraft] = useState(() => readCommunityFilter(search));
  const [open, setOpen] = useState("");
  const toggle = (k) => setOpen(open === k ? "" : k);
  const n = activeFilterCount(draft);
  const apply = () => navigate(`/community${communityQuery(draft)}`);
  return (
    <div data-m="M49" data-testid="community-filter-page">
      <V3Title title="Community-Ansicht filtern" intro="Begrenze die sichtbaren Beiträge nach Bereich und Art." />
      <SearchField id="cf-q" label="Beiträge durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value={draft.q} onChange={(q) => setDraft({ ...draft, q })} testid="community-filter-q" />
      <FilterBar testid="community-filter-bar" onReset={() => setDraft({ filter: "all", groupId: "", q: "" })} canReset={n > 0}><Pill tone="ink">Ausgewählte Filter</Pill></FilterBar>
      <ResultTable testid="community-filter-table" label="Filter">
        <ResultRow initial="B" title="Bereich" context={areaText(draft, groups.data)} onOpen={() => toggle("area")} expanded={open === "area"} testid="community-filter-area">
          {open === "area" && <AreaPicker draft={draft} setDraft={setDraft} groups={groups} />}
        </ResultRow>
        <ResultRow initial="A" title="Beitragsart" context="Noch nicht verfügbar" onOpen={() => toggle("kind")} expanded={open === "kind"} testid="community-filter-kind">
          {open === "kind" && <Note testid="community-filter-kind-note">Der gemeinsame Core unterscheidet noch keine Beitragsarten wie Fragen oder Treffen.</Note>}
        </ResultRow>
        <ResultRow initial="S" title="Sortierung" context="Reihenfolge wie vom Core geliefert" onOpen={() => toggle("sort")} expanded={open === "sort"} testid="community-filter-sort">
          {open === "sort" && <Note testid="community-filter-sort-note">Eine eigene Sortierung ist noch nicht verfügbar.</Note>}
        </ResultRow>
        <ResultRow initial="F" title="Aktive Auswahl" context={n ? `${n} Filter · Zurücksetzen möglich` : "Keine Filter"} testid="community-filter-active" />
      </ResultTable>
      <V3Actions className="mt-[21px]">
        <button type="button" onClick={apply} data-testid="community-filter-apply" className={V3_PRIMARY}>Filter anwenden</button>
        <Link to={`/community${search}`} data-testid="community-filter-cancel" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
    </div>
  );
}
